/*
 * Vehicle lookup component
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2021 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import {
  Vehicle,
  VehicleType
} from 'app/shared/models';
import { NotificationService } from 'app/shared/services/notification.service';

@Component ( {
    selector: 'reporter-vehicle-lookup',
    templateUrl: './vehicle-lookup.component.html',
    styleUrls: ['./vehicle-lookup.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => VehicleLookupComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => VehicleLookupComponent),
            multi: true
        },
    ],
    standalone: false
} )
export class VehicleLookupComponent implements AfterViewInit {

  @Input () incidentId: number;
  @Input () vehicleType: VehicleType;
  @Input () readOnly = false;
  @Input () disabled = false;
  @Input () showEmployeeName = false;
  @Input () useEmployeeId = false;
  @Input () maxlength: number;
  @Input() showLookup = true;
  @Output () vehicleChange = new EventEmitter<Vehicle> ();
  typeInValue: string;
  selected: Vehicle;
  showSearch = false;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private notificationService: NotificationService,
  ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return this.innerValue;
  }

  get required(): boolean {
    // eslint-disable-next-line no-prototype-builtins
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  };
  onTouched = () => {
  };

  ngAfterViewInit() {
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;

    if ( this.value && !this.typeInValue ) {
      this.typeInValue = this.value;
    }
  }

  writeValue( value: any ): void {
    this.innerValue = value;
    this.onChange ( value );
    this.onTouched ();
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.disabled = isDisabled;
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }
    if ( ( control.value === null || control.value === undefined ) && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    return null;
  }

  clear() {
    if ( this.disabled ) {
      return;
    }
    this.value = undefined;
    this.selected = undefined;
    this.typeInValue = undefined;
    this.vehicleChange.emit ( undefined );
    this.notificationService.clearAll ();
  }

  select( vehicle: any ) {
    if ( this.disabled ) {
      return;
    }
    this.selected = vehicle;
    const value = vehicle?.vin;
    this.typeInValue = value;
    this.writeValue ( value );
    this.vehicleChange.emit ( vehicle );
    this.close();
  }

  close() {
    this.showSearch = false;
    this.notificationService.clearAll ();
  }
}
